import React, { useState, useEffect } from 'react';

const API_URL = 'https://stock-api-production-913a.up.railway.app';

// 期別縮寫顯示：2025Q3 -> 25Q3，202607 -> 07月
function shortPeriod(p) {
  if (!p) return '';
  const s = String(p);
  const qm = s.match(/^(\d{4})Q([1-4])$/);
  if (qm) return `${qm[1].slice(2)}Q${qm[2]}`;
  const mm = s.match(/^(\d{4})(\d{2})$/);
  if (mm) return `${mm[2]}月`;
  return s;
}

// 只顯示最近3筆，但箭頭用多抓的前1筆去比較，讓3筆都能判斷「比上季」
function withUpFlags(data, showArrow) {
  const full = data && data.length ? data : [];
  const flagged = full.map((d, i) => ({
    period: d.period,
    value: d.value,
    up: !!(showArrow && i > 0 && d.value != null && full[i - 1] && full[i - 1].value != null && d.value > full[i - 1].value),
  }));
  const display = flagged.slice(-3);
  while (display.length < 3) display.unshift({ period: '', value: null, up: false });
  return display;
}

function MetricRow({ label, data, unit, colorMode, showArrow }) {
  const items = withUpFlags(data, showArrow);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', fontSize: 11, marginBottom: 5 }}>
      <span style={{ color: '#d8ecff', width: 82, flexShrink: 0, fontWeight: 700, fontSize: 11 }}>{label}</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 8px', flex: 1, minWidth: 0 }}>
        {items.map((d, i) => {
          const v = d.value;
          let color = '#c8d2dc';
          if (colorMode === 'signed' && v != null) color = v >= 0 ? '#f04545' : '#33b06a';
          return (
            <span key={i} style={{ color, fontWeight: 600, whiteSpace: 'nowrap', fontSize: 11 }}>
              <span style={{ color: '#4a6070', fontSize: 9, fontWeight: 500 }}>{shortPeriod(d.period)} </span>
              {v == null ? '-' : `${v}${unit}`}
              {d.up && <span style={{ color: '#ffe100', marginLeft: 2, fontWeight: 900 }}>▲</span>}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ROIC箭頭數量決定外框顏色：3個=紅、2個=黃、1個=綠、0個=不圈色
function roicBorderColor(data) {
  const items = withUpFlags(data, true);
  const latest = items[items.length - 1];
  if (!latest || latest.value == null || latest.value <= 5) return '#1a2a3c';
  const upCount = items.filter(d => d.up).length;
  if (upCount >= 3) return '#e04040';
  if (upCount === 2) return '#e0c020';
  if (upCount === 1) return '#30b060';
  return '#1a2a3c';
}

function GroupCell({ g, onClick, active }) {
  const borderColor = roicBorderColor(g.roic_q3);
  return (
    <div onClick={onClick} style={{
      background: active ? '#12202e' : '#0d1420',
      border: `2px solid ${borderColor}`, borderRadius: 6, padding: '12px 14px', cursor: 'pointer',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <span style={{ color: '#d8ecff', fontWeight: 800, fontSize: 15, letterSpacing: 0.3 }}>{g.group_name}</span>
        <span style={{ color: '#5a7890', fontSize: 11, fontWeight: 600 }}>{g.stock_count}檔</span>
      </div>
      <MetricRow label="ROIC(單季)" data={g.roic_q3} unit="%" colorMode="signed" showArrow />
      <MetricRow label="稅後營益率" data={g.nopat_margin_q3} unit="%" colorMode="signed" showArrow />
      <MetricRow label="投入資本/營收" data={g.ic_rev_q3} unit="" colorMode="none" showArrow />
      <MetricRow label="營收年增率" data={g.rev_growth_3m} unit="%" colorMode="signed" />
    </div>
  );
}

function MiniSeries({ data, unit, colorMode, showArrow }) {
  const items = withUpFlags(data, showArrow);
  const hasAny = items.some(d => d.value != null);
  if (!hasAny) return <span style={{ color: '#3a5060' }}>-</span>;
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      {items.map((d, i) => {
        const v = d.value;
        let color = '#c8d4e0';
        if (colorMode === 'signed' && v != null) color = v >= 0 ? '#f04545' : '#33b06a';
        return (
          <span key={i} style={{ marginRight: 10 }}>
            <span style={{ color: '#4a6070', fontSize: 9 }}>{shortPeriod(d.period)}</span>{' '}
            <span style={{ color, fontWeight: 700 }}>{v == null ? '-' : `${v}${unit}`}</span>
            {d.up && <span style={{ color: '#ffe100', marginLeft: 2, fontWeight: 900 }}>▲</span>}
          </span>
        );
      })}
    </span>
  );
}

function isRoicHot(data) {
  const items = withUpFlags(data, true);
  const upCount = items.filter(d => d.up).length;
  const latest = items[items.length - 1];
  return upCount >= 3 && latest && latest.value != null && latest.value > 5;
}

function GroupDetailTable({ type, name, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/roic-map/group-detail?type=${type}&name=${encodeURIComponent(name)}`)
      .then(r => r.json())
      .then(d => { setRows(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [type, name]);

  return (
    <div style={{ marginTop: 20, background: '#080b10', border: '1px solid #1a2a3c', borderRadius: 6, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ color: '#d8ecff', fontWeight: 800, fontSize: 15 }}>{name}（{rows.length}檔）個股明細</span>
        <button onClick={onClose} style={{
          background: 'transparent', border: '1px solid #2a4060', color: '#7ab0cc',
          borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12,
        }}>關閉</button>
      </div>
      {loading && <div style={{ color: '#4a6878', padding: '12px 0' }}>載入中...</div>}
      {!loading && rows.length === 0 && <div style={{ color: '#4a6878', padding: '12px 0' }}>沒有資料</div>}
      {!loading && rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12, minWidth: 900 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1a2a3c' }}>
                {['股票', '收盤價', 'ROIC(單季)', '稅後營益率(營業利益×0.8/營收)', '投入資本/營收', '營收年增率'].map(h => (
                  <th key={h} style={{ textAlign: 'left', color: '#7a8fa8', padding: '6px 10px', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const hot = isRoicHot(r.roic_q3);
                return (
                <tr key={r.ticker} style={{ borderBottom: '1px solid #12202e' }}>
                  <td style={{ padding: '7px 10px', color: hot ? '#ffe100' : '#d8ecff', fontWeight: 700, whiteSpace: 'nowrap' }}>{r.ticker} {r.name}</td>
                  <td style={{ padding: '7px 10px', color: '#c8d4e0', fontWeight: 700 }}>{r.close != null ? r.close : '-'}</td>
                  <td style={{ padding: '7px 10px' }}><MiniSeries data={r.roic_q3} unit="%" colorMode="signed" showArrow /></td>
                  <td style={{ padding: '7px 10px' }}><MiniSeries data={r.nopat_margin_q3} unit="%" colorMode="signed" showArrow /></td>
                  <td style={{ padding: '7px 10px' }}><MiniSeries data={r.ic_rev_q3} unit="" colorMode="none" showArrow /></td>
                  <td style={{ padding: '7px 10px' }}><MiniSeries data={r.rev_growth_3m} unit="%" colorMode="signed" /></td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 14px', borderRadius: 6, border: `1px solid ${active ? '#3a7ca8' : '#1a2a3c'}`,
      background: active ? '#0d2030' : 'transparent', color: active ? '#90c0dc' : '#4a6878',
      fontSize: 12, fontWeight: 600, cursor: 'pointer',
    }}>{children}</button>
  );
}

export default function ROICMap() {
  const [tab, setTab] = useState('industry');
  const [industries, setIndustries] = useState([]);
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null); // { type: 'industry'|'theme', name }

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/api/roic-map/industries`).then(r => r.json()).catch(() => []),
      fetch(`${API_URL}/api/roic-map/themes`).then(r => r.json()).catch(() => []),
    ]).then(([ind, th]) => {
      setIndustries(Array.isArray(ind) ? ind : []);
      setThemes(Array.isArray(th) ? th : []);
      setLoading(false);
    }).catch(e => { setErr(String(e)); setLoading(false); });
  }, []);

  const list = tab === 'industry' ? industries : themes;

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ color: '#c0e0f8', fontSize: 18, margin: 0 }}>🗺 ROIC地圖</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <TabBtn active={tab === 'industry'} onClick={() => { setTab('industry'); setSelected(null); }}>依產業分類（{industries.length}）</TabBtn>
          <TabBtn active={tab === 'theme'} onClick={() => { setTab('theme'); setSelected(null); }}>依概念股分類（{themes.length}）</TabBtn>
        </div>
      </div>

      {loading && <div style={{ color: '#4a6878', padding: '20px 0' }}>載入中...</div>}
      {err && <div style={{ color: '#e04040', padding: '20px 0' }}>載入失敗：{err}</div>}
      {!loading && !err && list.length === 0 && <div style={{ color: '#4a6878', padding: '20px 0' }}>目前沒有資料</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
        {list.map(g => (
          <GroupCell key={g.group_name} g={g}
            active={selected && selected.type === tab && selected.name === g.group_name}
            onClick={() => setSelected(
              selected && selected.type === tab && selected.name === g.group_name
                ? null
                : { type: tab, name: g.group_name }
            )}
          />
        ))}
      </div>

      {selected && (
        <GroupDetailTable type={selected.type} name={selected.name} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
